import { SubscriptionPlan } from '../types/subscription';
import { getPlanById } from '../constants/subscriptionPlans';
import { LocalUsageStats } from './localUsageService';

export interface PlanLimitation {
  type: 'office' | 'client' | 'file' | 'team_member';
  currentCount: number;
  maxAllowed: number;
  isUnlimited: boolean;
  isExceeded: boolean;
  exceededCount: number;
  message: string;
}

export interface PlanLimitations {
  offices: PlanLimitation;
  clients: PlanLimitation;
  files: PlanLimitation;
  teamMembers: PlanLimitation;
  overallExceeded: boolean;
  exceededLimits: string[];
}

class PlanLimitationsService {
  /**
   * Calculate plan limitations based on current subscription and usage
   */
  calculatePlanLimitations(
    planId: string,
    usageStats: LocalUsageStats
  ): PlanLimitations {
    const plan = getPlanById(planId);
    if (!plan) {
      throw new Error('Plan not found');
    }

    const offices = this.calculateLimitation(
      'office',
      usageStats.officesCount,
      plan.maxOffices,
      'Offices'
    );

    const clients = this.calculateLimitation(
      'client',
      usageStats.clientsCount,
      plan.maxClientsTotal,
      'Clients'
    );

    const files = this.calculateLimitation(
      'file',
      usageStats.filesCount,
      plan.maxFilesTotal,
      'Files'
    );

    const teamMembers = this.calculateLimitation(
      'team_member',
      usageStats.teamMembersCount || 0,
      plan.maxTeamMembers || 0,
      'Team Members'
    );

    const exceededLimits = [offices, clients, files, teamMembers]
      .filter(limitation => limitation.isExceeded)
      .map(limitation => limitation.type);

    return {
      offices,
      clients,
      files,
      teamMembers,
      overallExceeded: exceededLimits.length > 0,
      exceededLimits
    };
  }

  /**
   * Calculate individual limitation
   */
  private calculateLimitation(
    type: 'office' | 'client' | 'file' | 'team_member',
    currentCount: number,
    maxAllowed: number,
    displayName: string
  ): PlanLimitation {
    const isUnlimited = maxAllowed === -1;
    const isExceeded = !isUnlimited && currentCount > maxAllowed;
    const exceededCount = isExceeded ? currentCount - maxAllowed : 0;

    let message = '';
    if (isUnlimited) {
      message = `${displayName} are unlimited`;
    } else if (isExceeded) {
      message = `${exceededCount} ${displayName.toLowerCase()} exceed your plan limit (${maxAllowed} max)`;
    } else {
      message = `${currentCount}/${maxAllowed} ${displayName.toLowerCase()} used`;
    }

    return {
      type,
      currentCount,
      maxAllowed,
      isUnlimited,
      isExceeded,
      exceededCount,
      message
    };
  }

  /**
   * Check if a specific action is allowed based on plan limitations
   */
  isActionAllowed(
    planId: string,
    actionType: 'create_office' | 'create_client' | 'create_file' | 'add_team_member',
    currentCount: number
  ): boolean {
    const plan = getPlanById(planId);
    if (!plan) return false;

    switch (actionType) {
      case 'create_office':
        return plan.maxOffices === -1 || currentCount < plan.maxOffices;
      case 'create_client':
        return plan.maxClientsTotal === -1 || currentCount < plan.maxClientsTotal;
      case 'create_file':
        return plan.maxFilesTotal === -1 || currentCount < plan.maxFilesTotal;
      case 'add_team_member':
        return plan.maxTeamMembers === -1 || currentCount < (plan.maxTeamMembers || 0);
      default:
        return false;
    }
  }

  /**
   * Get disabled items based on plan limitations
   */
  getDisabledItems(
    planId: string,
    usageStats: LocalUsageStats,
    allItems: any[],
    itemType: 'office' | 'client' | 'file' | 'team_member'
  ): { enabled: any[]; disabled: any[] } {
    const limitations = this.calculatePlanLimitations(planId, usageStats);
    const limitation = limitations[itemType === 'team_member' ? 'teamMembers' : `${itemType}s` as keyof typeof limitations] as PlanLimitation;

    if (limitation.isUnlimited) {
      return { enabled: allItems, disabled: [] };
    }

    const enabledItems = allItems.slice(0, limitation.maxAllowed);
    const disabledItems = allItems.slice(limitation.maxAllowed);

    return { enabled: enabledItems, disabled: disabledItems };
  }

  /**
   * Get limitation message for UI display
   */
  getLimitationMessage(
    planId: string,
    usageStats: LocalUsageStats,
    itemType: 'office' | 'client' | 'file' | 'team_member'
  ): string {
    const limitations = this.calculatePlanLimitations(planId, usageStats);
    const limitation = limitations[itemType === 'team_member' ? 'teamMembers' : `${itemType}s` as keyof typeof limitations] as PlanLimitation;

    return limitation.message;
  }

  /**
   * Check if user should see upgrade prompt
   */
  shouldShowUpgradePrompt(
    planId: string,
    usageStats: LocalUsageStats
  ): boolean {
    const limitations = this.calculatePlanLimitations(planId, usageStats);
    return limitations.overallExceeded;
  }

  /**
   * Get suggested plan for upgrade
   */
  getSuggestedUpgradePlan(planId: string): string | null {
    switch (planId) {
      case 'free':
        return 'basic';
      case 'basic':
        return 'professional';
      default:
        return null;
    }
  }
}

// Export singleton instance
export const planLimitationsService = new PlanLimitationsService();
export default planLimitationsService;
